// Shared marketing copy for the new EzeitPro modules (DMS, Finance, Inventory + GRN, AI Assistant).
// Used by the home page ("Built for the whole business") and the Car Service Centre page.
//
// Screenshots: each `shot.base` is a path without device or size suffix; DeviceCollage loads the
// desktop, tablet and phone captures of that screen (`<base>-desktop-1400.webp` ...). They are
// produced by utilstop_apiengine/scripts/capture-marketing-shots.mjs from a fictitious demo dealership.
//
// All four modules are Enterprise-only and sold on request (see pricingData.js).

const SHOT_DIR = '/image/website';
export const shotBase = (name) => `${SHOT_DIR}/${name}`;

// Hero composite and the existing-product screens that replace mock cards on the home page.
export const HERO_SHOT = { base: shotBase('dms-dashboard'), alt: 'Dealership dashboard with sales, workshop and stock KPIs' };
export const CORE_SHOTS = {
  dashboard: { base: shotBase('core-dashboard'), alt: 'Daily dashboard with today\'s bookings and crew utilisation', caption: 'Daily dashboard' },
  crew: { base: shotBase('core-crew-schedule'), alt: 'Crew schedule showing jobs assigned to each crew member', caption: 'Crew schedule' },
  invoice: { base: shotBase('core-payments'), alt: 'Payments received against bookings, by method and date', caption: 'Payments' },
  analytics: { base: shotBase('core-analytics'), alt: 'Analytics dashboard with revenue and bookings', caption: 'Analytics' },
};

// ── AI Assistant wording lives in ONE place so it can be changed in one edit. ──
// Booking / rescheduling / cancelling by the AI is described here as a capability, per the
// product owner's decision. The build spec (docs/utilstop-ai-agent-build-spec.md) schedules it as
// Phase 2; if the live product differs, change `AI_BOOKING_LINE` and nothing else.
export const AI_BOOKING_LINE = 'Books, reschedules and cancels appointments, always with the customer\'s confirmation';

export const AI_COPY = {
  headline: 'Your customer support never sleeps',
  blurb:
    'The AI Assistant answers your customers on your own WhatsApp number around the clock: services, prices, available slots and FAQs, in the language they write in. Anything it cannot settle goes to your team with the full conversation attached.',
  bullets: [
    '24x7 replies on your own WhatsApp number',
    'Quotes your real services, prices and open slots',
    AI_BOOKING_LINE,
    'Understands voice notes; English and Tamil today',
    'Hands over to your team, or books a callback, when a person is needed',
    'Starts in review mode so staff approve drafts, then goes live. Switch it off any time',
  ],
};

export const MODULES = [
  {
    id: 'dms',
    eyebrow: 'Dealership Management',
    title: 'Sell, service and stock vehicles from one system',
    blurb:
      'From the first enquiry to the delivered car and every service visit after it, your sales desk, workshop and parts counter work from the same record.',
    bullets: [
      'Quotations, sales orders, delivery checklist and tax invoice',
      'Repair orders by pay type, with a technician board and clocking',
      'Warranty claims that keep the failed parts attached',
      'Body shop estimates with a split insurer and customer invoice',
      'Parts sales, counter sales, F&I deals and fleet agreements',
      'Service reminders, recalls and customer satisfaction surveys',
    ],
    enterprise: true,
    shot: { base: shotBase('dms-repair-orders'), alt: 'Repair orders list with job status, pay type and technician', caption: 'Repair orders' },
  },
  {
    id: 'finance',
    eyebrow: 'Finance & Accounting',
    title: 'Books that write themselves, built for UAE VAT',
    blurb:
      'Every invoice, receipt, bill and stock movement posts its own journal, so the ledger is up to date without double entry.',
    bullets: [
      'Automatic journals from sales, purchases, payroll and stock',
      'Trial balance, profit and loss and balance sheet',
      'UAE VAT 201 returns, including the margin scheme for used vehicles',
      'Bank reconciliation, post-dated cheques, petty cash and cash drawers',
      'Supplier bills matched to goods receipts',
      'Fixed assets with monthly depreciation and an approvals inbox',
    ],
    enterprise: true,
    shot: { base: shotBase('fin-dashboard'), alt: 'Finance dashboard with receivables, payables and cash position', caption: 'Finance dashboard' },
  },
  {
    id: 'inventory',
    eyebrow: 'Inventory, PO & GRN',
    title: 'Know what you ordered, what arrived and what it cost',
    blurb:
      'Raise purchase orders, receive goods against them in full or in part, and see the landed cost land in your stock and your books.',
    bullets: [
      'Purchase orders with approval limits',
      'Goods receipts (GRN), partial deliveries and purchase returns',
      'Landed cost spread across what you received',
      'Multiple warehouses, bins and min/max levels',
      'Batch and expiry, tyre DOT codes and serial numbers',
      'Stock take with freeze, recount and approval, plus reorder suggestions',
    ],
    enterprise: true,
    shot: { base: shotBase('inv-grn'), alt: 'Goods receipt (GRN) against a purchase order', caption: 'Goods receipt' },
  },
  {
    id: 'ai',
    eyebrow: 'AI Assistant',
    title: AI_COPY.headline,
    blurb: AI_COPY.blurb,
    bullets: AI_COPY.bullets,
    enterprise: true,
    visual: 'chat',
    shot: { base: shotBase('ai-conversations'), alt: 'AI Assistant conversations inbox', caption: 'Conversations inbox' },
  },
];

export const getModule = (id) => MODULES.find((m) => m.id === id);

// Capability tiles for the Car Service Centre page.
export const CAR_HIGHLIGHTS = [
  { icon: 'Wrench', title: 'Repair orders & job cards', body: 'Open a job per vehicle with labour, parts and sublets, split by customer, warranty or insurer pay type.' },
  { icon: 'Users', title: 'Technician board', body: 'See who is on which job, clock technicians on and off, and spot idle bays at a glance.' },
  { icon: 'ShieldCheck', title: 'Warranty claims', body: 'Track warranty claims through to settlement and keep the failed parts attached until they are cleared.' },
  { icon: 'CarFront', title: 'Body shop & insurance', body: 'Estimate, get insurer approval and bill the insurer and the customer separately from one job.' },
  { icon: 'Package', title: 'Parts & counter sales', body: 'Sell parts across the counter or against a job, with back orders, issue and returns.' },
  { icon: 'Boxes', title: 'Purchase orders & GRN', body: 'Order from suppliers, receive against the PO, and watch landed cost flow into stock.' },
  { icon: 'BellRing', title: 'Reminders, recalls & CSI', body: 'Bring customers back for the next service, act on recalls, and measure satisfaction after every visit.' },
  { icon: 'Landmark', title: 'Finance & UAE VAT', body: 'Every invoice posts to the ledger, with VAT 201 returns and bank reconciliation built in.' },
  { icon: 'Bot', title: 'AI Assistant on WhatsApp', body: 'Customers get instant answers on prices and slots, day or night, while your team is on the shop floor.' },
];

// Screenshot sections for the Car Service Centre page (ids refer to MODULES entries,
// plus workshop-specific captions).
export const CAR_SHOWCASE = [
  {
    id: 'car-workshop',
    eyebrow: 'Workshop',
    title: 'Every job, bay and technician in view',
    blurb: 'Replace the whiteboard and the paper job card. Service advisors open repair orders, technicians clock on, and the customer is updated without a phone call.',
    bullets: [
      'Repair orders by pay type with labour rates and sublets',
      'Technician board with clocking and QC before release',
      'Gate pass and split invoicing at handover',
      'Automatic confirmation and ready-for-collection messages',
    ],
    enterprise: true,
    shot: { base: shotBase('dms-technician-board'), alt: 'Technician board showing jobs per technician', caption: 'Technician board' },
  },
  {
    id: 'car-warranty',
    eyebrow: 'Warranty & after-sales',
    title: 'Get warranty paid, and customers back',
    blurb: 'Warranty claims keep the failed part with the claim, and the after-sales dashboard shows who is due for service.',
    bullets: [
      'Warranty claims tracked through to settlement',
      'Failed parts retained until the claim clears',
      'Service reminders, recalls and CSI surveys',
    ],
    enterprise: true,
    shot: { base: shotBase('dms-warranty-claims'), alt: 'Warranty claims list', caption: 'Warranty claims' },
  },
  {
    id: 'car-parts',
    eyebrow: 'Parts, PO & GRN',
    title: 'Never lose track of a part or its cost',
    blurb: 'Order, receive and issue parts with batch, serial and tyre DOT tracking, then count the shelves with a proper stock take.',
    bullets: [
      'Purchase orders and goods receipts, with partial deliveries',
      'Landed cost on every received line',
      'Low-stock alerts and reorder suggestions',
      'Stock take with recount and approval',
    ],
    enterprise: true,
    shot: { base: shotBase('inv-grn'), alt: 'Goods receipt against a purchase order', caption: 'Goods receipt' },
  },
  {
    id: 'car-finance',
    eyebrow: 'Finance',
    title: 'From job card to VAT return, no re-keying',
    blurb: 'Invoices, supplier bills, stock movements and payroll post their own journals. Close the month with reports you can trust.',
    bullets: [
      'UAE VAT 201 with the used-vehicle margin scheme',
      'Bank reconciliation and post-dated cheques',
      'Profit and loss, balance sheet and trial balance',
    ],
    enterprise: true,
    shot: { base: shotBase('fin-vat-return'), alt: 'UAE VAT 201 return worksheet', caption: 'VAT 201' },
  },
  {
    id: 'car-ai',
    eyebrow: 'AI Assistant',
    title: AI_COPY.headline,
    blurb: AI_COPY.blurb,
    bullets: AI_COPY.bullets,
    enterprise: true,
    visual: 'chat',
    shot: { base: shotBase('ai-conversations'), alt: 'AI Assistant conversations inbox', caption: 'Conversations inbox' },
  },
];

// Every shot base referenced above, for the capture script and the data test.
export const ALL_SHOT_BASES = Array.from(
  new Set([HERO_SHOT.base, ...Object.values(CORE_SHOTS).map((s) => s.base), ...[...MODULES, ...CAR_SHOWCASE].map((m) => m.shot.base)])
);
